import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  DMCoreSpaceOperatorData,
  DMCoreSpaceOperatorLoginData,
  DMCoreSpaceOperatorLoginResponse,
  DMCoreSpaceOperatorTokenRefreshResponse
} from '#src/interfaces/DMCoreSpaceOperator'
import { isDMCoreSpaceOperatorLoginResponse } from '#src/utils/typeGuards'

const OPERATOR_LOGIN_DATA_STORAGE_KEY = 'operator_login_data'

export const useDmCoreSpaceOperatorStore = defineStore('dmCoreSpaceOperatorStore', () => {
  const operatorLoginData = ref<DMCoreSpaceOperatorLoginData | null>(
    JSON.parse(sessionStorage.getItem(OPERATOR_LOGIN_DATA_STORAGE_KEY) ?? 'null')
  )
  const operatorData = ref<DMCoreSpaceOperatorData | null>(null)

  const operatorDisplayName = computed(() =>
    operatorData.value ? `${operatorData.value.first_name} ${operatorData.value.last_name}` : null
  )

  const setOperatorLoginData = (
    data: DMCoreSpaceOperatorLoginResponse | DMCoreSpaceOperatorTokenRefreshResponse
  ) => {
    if (isDMCoreSpaceOperatorLoginResponse(data)) {
      const { access_token, refresh_token, operator_uuid } = data
      const loginData: DMCoreSpaceOperatorLoginData = {
        access_token,
        refresh_token,
        operator_uuid
      }

      operatorLoginData.value = loginData
      sessionStorage.setItem(OPERATOR_LOGIN_DATA_STORAGE_KEY, JSON.stringify(loginData))

      return
    }

    const { access_token, refresh_token } = data
    const refreshData: DMCoreSpaceOperatorLoginData = {
      operator_uuid: operatorLoginData.value!.operator_uuid,
      access_token,
      refresh_token
    }

    operatorLoginData.value = refreshData
    sessionStorage.setItem(OPERATOR_LOGIN_DATA_STORAGE_KEY, JSON.stringify(refreshData))
  }

  const removeOperatorLoginData = () => {
    operatorLoginData.value = null
    sessionStorage.removeItem(OPERATOR_LOGIN_DATA_STORAGE_KEY)
  }

  const setOperatorData = (data: DMCoreSpaceOperatorData) => {
    operatorData.value = data
  }

  const removeOperatorData = () => {
    operatorData.value = null
  }

  const isOperatorLoggedIn = computed(() => !!operatorLoginData.value?.access_token)

  return {
    operatorLoginData,
    operatorData,
    operatorDisplayName,
    setOperatorLoginData,
    isOperatorLoggedIn,
    setOperatorData,
    removeOperatorLoginData,
    removeOperatorData
  }
})
